import React, { useMemo } from "react";
import classes from "../TransInter.module.scss";
import {
  BackgroundImage,
  Box,
  Button,
  Flex,
  Image,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import background from "assets/images/pngs/background.png";
import image11 from "@/assets/images/pngs/image11.png";
import { IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@/stores";
import { useTransFormContext } from "../TransInterFormContext";
import { TransInterStepFiveProps } from "../types";
import FileBadge from "../components/FileBadge";
import { downloadFormDocument } from "../documentUtils";
import { useGetIntlTransferPurposeQuery } from "@/services/internationalTransferAPI";

const RowLine: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Flex justify="space-between" align="center" gap={20}>
    <Text fz={14} c="#6B7280">
      {label}
    </Text>
    <Text fz={14} fw={600} c="#212936" ta="right">
      {value}
    </Text>
  </Flex>
);

// Lấy phần số từ chuỗi hiển thị của BE ("5 USD", "105 USD"...) khi không có số gốc.
const parseAmountFromDisplay = (display?: string): number => {
  if (!display) return NaN;
  const digits = display.replace(/[^\d.]/g, "");
  return digits ? Number(digits) : NaN;
};

// Định dạng tiền ngoại tệ theo Figma: luôn 2 chữ số thập phân.
// currencyFirst = true  -> "USD 105.00" (Tổng số tiền giao dịch)
// currencyFirst = false -> "5.00 USD"  (Số tiền phí)
const formatForeignAmount = (
  numeric: number | undefined,
  display: string | undefined,
  currency: string,
  currencyFirst: boolean,
): string => {
  const amount =
    typeof numeric === "number" && !Number.isNaN(numeric)
      ? numeric
      : parseAmountFromDisplay(display);
  if (Number.isNaN(amount)) return display || "—";
  const cur = (
    currency ||
    display?.match(/[A-Za-z]{2,3}/)?.[0] ||
    ""
  ).toUpperCase();
  const formatted = amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currencyFirst
    ? `${cur} ${formatted}`.trim()
    : `${formatted} ${cur}`.trim();
};

const formatCommitmentDate = (value?: Date | null): string => {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const TransInterStepFive: React.FC<TransInterStepFiveProps> = ({
  initData,
  confirmData,
  onGoHome,
  onCreateNew,
}) => {
  const { t, i18n } = useTranslation();
  const form = useTransFormContext();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: purposeData } = useGetIntlTransferPurposeQuery();
  const purposeGroups = purposeData?.body?.purposeGroups || [];

  // Chọn tên theo ngôn ngữ hiện tại (name = tiếng Việt, nameEn = tiếng Anh).
  const pickName = (o: { name: string; nameEn?: string }) =>
    (i18n.language || "vi").startsWith("en") && o.nameEn ? o.nameEn : o.name;

  const purposeTypeLabel = useMemo(() => {
    const g = purposeGroups.find((p) => p.code === form.values.purposeType);
    return g ? pickName(g) : form.values.purposeType || "—";
  }, [purposeGroups, form.values.purposeType, i18n.language]);
  const purposeDetailLabel = useMemo(() => {
    const parent = purposeGroups.find((p) => p.code === form.values.purposeType);
    const item = parent?.items?.find(
      (it) => it.code === form.values.purposeDetail,
    );
    return item ? pickName(item) : form.values.purposeDetail || "—";
  }, [
    purposeGroups,
    form.values.purposeType,
    form.values.purposeDetail,
    i18n.language,
  ]);

  const currency = form.values.currency || "";
  // Số tiền phí: định dạng "5.00 USD" (2 số thập phân, đơn vị đứng sau).
  const feeDisplay = formatForeignAmount(
    initData?.feeAmountForeign,
    initData?.feeAmountForeignDisplay || initData?.feeAmountDisplay,
    currency,
    false,
  );
  // Tổng số tiền giao dịch = tiền chuyển (KHÔNG cộng phí), nhất quán với màn đầu.
  const totalDisplay = formatForeignAmount(
    Number(String(form.values.amount ?? "").replace(/[^\d.]/g, "")) || undefined,
    undefined,
    currency,
    false,
  );
  const debtLater = form.values.debtDocumentLater;
  const documents = form.values.documents || [];

  return (
    <Stack align="center" spacing={20}>
      {/* Khối thông tin giao dịch */}
      <Box className={classes.sourceAccount} w={782}>
        {/* Banner trạng thái */}
        <BackgroundImage src={background} radius="md">
          <Stack spacing={4} style={{ alignItems: "center" }} p={16}>
            <Box w={120} h={80}>
              <Image src={image11} w={120} />
            </Box>
            <Text fz={16} fw={700} sx={{ color: "white" }}>
              {t("TRANS_APPROVED")}
            </Text>
          </Stack>
        </BackgroundImage>

        <Space h={20} />
        <Stack spacing={14}>
          <RowLine
            label={t("TRANSACTION_CODE")}
            value={confirmData?.transId || confirmData?.refId || "—"}
          />
          <RowLine
            label={t("TRANSFER_TYPE")}
            value={t("INTERNATIONAL_TRANSFER") || t("INTERNATIONAL_TRANSFER")}
          />
          <RowLine label={t("FEE_AMOUNT")} value={feeDisplay} />
          <RowLine label={t("TOTAL_TRANSACTION_AMOUNT")} value={totalDisplay} />
          <RowLine label={t("PURPOSE_TYPE")} value={purposeTypeLabel} />
          <RowLine label={t("PURPOSE_OF_MONEY_TRANSFER")} value={purposeDetailLabel} />
          {/* #55999: hai dong nay chi co nghia khi giao dich CO no chung tu bo sung sau.
              Truoc day dong "No chung tu bo sung sau" render vo dieu kien nen giao dich
              khong no van hien them mot dong "No" thua. */}
          {debtLater && (
            <>
              <RowLine
                label={t("DOCUMENTS_OWED_LATER")}
                value={t("YES")}
              />
              <RowLine
                label={t("DOCUMENT_SUPPLEMENT_DEADLINE")}
                value={formatCommitmentDate(form.values.commitmentDate)}
              />
            </>
          )}
          <Flex justify="space-between" align="center" gap={20}>
            <Text fz={14} c="#6B7280">
              {t("STATUS")}
            </Text>
            <Text
              fz={13}
              fw={600}
              c="#B45309"
              style={{
                backgroundColor: "#FEF3C7",
                borderRadius: 20,
                padding: "2px 12px",
              }}
            >
              {t("WAITING_APPROVE")}
            </Text>
          </Flex>
          <RowLine label={t("DATE_CREATED")} value={confirmData?.transDateTime || "—"} />
          <RowLine label={t("INITIATOR")} value={user?.fullName || "—"} />
        </Stack>

        {documents.length > 0 && (
          <>
            <Space h={18} />
            <Text fw={600} fz={14} c="#212936">
              {t("DOCUMENT_FILE_LIST")}
            </Text>
            <Space h={10} />
            <Stack spacing={12}>
              {documents.map((doc) => (
                <Flex
                  key={`${doc.documentName}-${doc.fileContent.slice(0, 12)}`}
                  justify="space-between"
                  align="center"
                >
                  <Flex gap={10} align="center">
                    <FileBadge fileType={doc.fileType} size={30} />
                    <Text fz={14} c="#157CC1">
                      {doc.documentName}
                    </Text>
                  </Flex>
                  <IconDownload
                    size={20}
                    color="#2A81D0"
                    style={{ cursor: "pointer" }}
                    onClick={() => downloadFormDocument(doc)}
                  />
                </Flex>
              ))}
            </Stack>
          </>
        )}
      </Box>

      {/* Khối nút điều hướng — tách riêng khỏi khối thông tin giao dịch (theo mockup) */}
      <Flex w={782} gap={16} justify="center">
        <Button
          onClick={onGoHome}
          sx={() => ({
            flex: 1,
            backgroundColor: "#FFFFFF",
            color: "#005F9E",
            border: "1px solid #005F9E",
            borderRadius: "8px",
            padding: "10px 32px",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#F4F8FC" },
          })}
        >
          {t("BACK_TO_HOME") || t("BACK_TO_HOME")}
        </Button>
        <Button
          type="submit"
          onClick={onCreateNew}
          sx={() => ({
            flex: 1,
            backgroundColor: "#005F9E",
            color: "white",
            borderRadius: "8px",
            padding: "10px 32px",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#0A538A" },
          })}
        >
          {t("CREATE_NEW_TRANSACTION") || t("CREATE_NEW_TRANSACTION")}
        </Button>
      </Flex>
    </Stack>
  );
};

export default TransInterStepFive;
